import React from 'react';
import { Add as AddIcon, DescriptionOutlined as DescriptionOutlinedIcon } from '@mui/icons-material';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import FlowSection from '../../components/root/shared/FlowSection.tsx';
import ClientDocumentRow from '../../client/step2/components/ClientDocumentRow.tsx';
import { guaranteesClientStep2Styles } from '../../client/step2/styles.ts';
import type { TClientUploadDocumentRow } from '../../client/step2/types.ts';
import { MANAGER_APPROVAL_LETTER_MANUAL_SIGN_TEXT } from './constants.ts';
import type { TManagerManualSignDocumentRow } from './utils.ts';

type TManualSignedApprovalLetterSectionProps = {
  document: TManagerManualSignDocumentRow | null;
  uploadingDocumentId: string | null;
  downloadingDocumentId: string | null;
  deletingDocumentId: string | null;
  actionMenuAnchorEl: HTMLElement | null;
  actionMenuDocumentId: string | null;
  onAddDocument: (file?: File | null) => void;
  onActionMenuOpen: (event: React.MouseEvent<HTMLButtonElement>, documentId: string) => void;
  onActionMenuClose: () => void;
  onDownload: (document: TClientUploadDocumentRow, forceDownload: boolean) => void;
  onDeleteUploadedDocument: (document: TClientUploadDocumentRow) => void;
};

const ManualSignedApprovalLetterSection: React.FC<
  TManualSignedApprovalLetterSectionProps
> = ({
  document,
  uploadingDocumentId,
  downloadingDocumentId,
  deletingDocumentId,
  actionMenuAnchorEl,
  actionMenuDocumentId,
  onAddDocument,
  onActionMenuOpen,
  onActionMenuClose,
  onDownload,
  onDeleteUploadedDocument,
}) => (
  <FlowSection
    step={MANAGER_APPROVAL_LETTER_MANUAL_SIGN_TEXT.uploadStep}
    title={MANAGER_APPROVAL_LETTER_MANUAL_SIGN_TEXT.uploadTitle}
    description={MANAGER_APPROVAL_LETTER_MANUAL_SIGN_TEXT.uploadDescription}
  >
    <Box sx={guaranteesClientStep2Styles.documentsUploadStack}>
      <Box sx={guaranteesClientStep2Styles.documentsList}>
        <Box sx={guaranteesClientStep2Styles.documentsHeader}>
          <Typography sx={guaranteesClientStep2Styles.documentsHeaderCell}>
            {MANAGER_APPROVAL_LETTER_MANUAL_SIGN_TEXT.documentColumnLabel}
          </Typography>
          <Typography sx={guaranteesClientStep2Styles.documentsHeaderCell}>
            {MANAGER_APPROVAL_LETTER_MANUAL_SIGN_TEXT.actionsColumnLabel}
          </Typography>
        </Box>

        {document ? (
          <ClientDocumentRow
            document={document}
            isLast
            isUploading={uploadingDocumentId === document.id}
            isDownloading={downloadingDocumentId === document.id}
            isDeleting={deletingDocumentId === document.id}
            isMenuOpen={actionMenuDocumentId === document.id}
            actionMenuAnchorEl={actionMenuAnchorEl}
            onActionMenuOpen={(event) => onActionMenuOpen(event, document.id)}
            onActionMenuClose={onActionMenuClose}
            onDownload={onDownload}
            onDeleteUploadedDocument={onDeleteUploadedDocument}
          />
        ) : (
          <Box sx={guaranteesClientStep2Styles.documentRow(true)}>
            <Box sx={guaranteesClientStep2Styles.documentNameWrap}>
              <DescriptionOutlinedIcon sx={guaranteesClientStep2Styles.documentIcon} />
              <Typography component="span" sx={guaranteesClientStep2Styles.documentFileName}>
                {MANAGER_APPROVAL_LETTER_MANUAL_SIGN_TEXT.emptyDocumentName}
              </Typography>
            </Box>
            <Box sx={guaranteesClientStep2Styles.documentActions} />
          </Box>
        )}
      </Box>

      {!document && (
        <Box sx={guaranteesClientStep2Styles.documentsUploadActions}>
          <Button
            component="label"
            variant="outlined"
            disabled={!!uploadingDocumentId}
            startIcon={uploadingDocumentId ? <CircularProgress size={18} /> : <AddIcon />}
            sx={guaranteesClientStep2Styles.documentsAddButton}
          >
            {MANAGER_APPROVAL_LETTER_MANUAL_SIGN_TEXT.addDocumentButton}
            <input
              hidden
              type="file"
              onChange={(event) => {
                onAddDocument(event.target.files?.[0]);
                event.target.value = '';
              }}
            />
          </Button>
        </Box>
      )}
    </Box>
  </FlowSection>
);

export default ManualSignedApprovalLetterSection;
